import { UNIT_LABELS, type UnitType } from '@bienenkampf/shared';
import { Badge, Button, Card } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useNow } from '../hooks/useNow.js';
import { formatAbsRel, formatDuration, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

/**
 * Angriffsplaner (Konzept 9): gemeinsame Liste geplanter Zuege und Verteidigungsanfragen.
 * Jedes Mitglied sieht seine eigene Flugzeit je Stock und ob es rechtzeitig ankommt.
 */
export function PlannerCard({ worldId }: { worldId: number }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const now = useNow(10_000);
  const plans = useQuery({
    ...trpc.alliance.plans.queryOptions({ worldId }),
    refetchInterval: 60_000,
  });
  const close = useMutation(
    trpc.alliance.closePlan.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.alliance.pathKey() }),
    }),
  );
  if (!plans.data?.canPlan) return null;
  const items = plans.data.items;
  return (
    <Card title={`Planer (${items.length})`}>
      {items.length === 0 && (
        <p className="text-sm text-stone-400">
          Keine Einträge. Angriffe trägst du vom Stock-Panel auf der Karte ein,
          Verteidigungsanfragen unter „Züge“ bei eingehenden Angriffen.
        </p>
      )}
      <ul className="flex flex-col gap-3 text-sm">
        {items.map((p) => {
          const late = p.deadline.getTime() < now.getTime();
          return (
            <li key={p.id} className="rounded-lg bg-stone-900 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={p.kind === 'DEFENSE' ? 'red' : 'amber'}>
                  {p.kind === 'DEFENSE' ? '🛡 Verteidigung' : '⚔ Angriff'}
                </Badge>
                <Link
                  to="/w/$worldId"
                  params={{ worldId: String(worldId) }}
                  search={{ q: p.target.q, r: p.target.r }}
                  className="font-medium text-amber-300"
                >
                  {p.target.name} [{p.target.q}|{p.target.r}]
                </Link>
                <span className="text-xs text-stone-400">
                  {p.target.ownerName ?? '–'} · von {p.creatorName}
                </span>
                <span
                  className={`ml-auto text-xs tabular-nums ${late ? 'text-red-400' : 'text-stone-300'}`}
                >
                  {p.kind === 'DEFENSE' ? 'bis' : 'Ankunft'} {formatAbsRel(p.deadline, now)}
                </span>
              </div>
              {p.note && <div className="mt-1 text-xs text-stone-300">{p.note}</div>}
              <ul className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                {p.mine.map((h) => (
                  <li key={h.hiveId} className="flex flex-wrap items-center gap-1 text-stone-300">
                    <span className="text-stone-500">{h.hiveName}:</span>
                    <span className={h.guardInTime ? 'text-emerald-300' : 'text-red-300'}>
                      Wächterinnen {formatDuration(h.guardMinutes * 60)} {h.guardInTime ? '✓' : '✗'}
                    </span>
                    <span className="text-stone-600">·</span>
                    <span className={h.warriorInTime ? 'text-emerald-300' : 'text-red-300'}>
                      Kriegerinnen {formatDuration(h.warriorMinutes * 60)}{' '}
                      {h.warriorInTime ? '✓' : '✗'}
                    </span>
                  </li>
                ))}
              </ul>
              {p.responses.length > 0 && (
                <div className="mt-2 text-xs text-stone-400">
                  Antworten:{' '}
                  {p.responses
                    .map(
                      (r) =>
                        `${r.ownerName} (${Object.entries(r.units)
                          .filter(([, n]) => n > 0)
                          .map(([t, n]) => `${formatNumber(n)} ${UNIT_LABELS[t as UnitType]}`)
                          .join(
                            ', ',
                          )}${r.status === 'STATIONED' ? ', stationiert' : `, Ankunft ${formatAbsRel(r.arrivesAt, now)}`})`,
                    )
                    .join(' · ')}
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <Link
                  to="/w/$worldId"
                  params={{ worldId: String(worldId) }}
                  search={{ q: p.target.q, r: p.target.r }}
                >
                  <Button size="sm" variant="ghost">
                    {p.kind === 'DEFENSE' ? '🛡 Unterstützung schicken' : '⚔ Zum Ziel'}
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => close.mutate({ worldId, planId: p.id })}
                  loading={close.isPending}
                >
                  Erledigt
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      {close.error && <p className="mt-2 text-sm text-red-400">{close.error.message}</p>}
    </Card>
  );
}

/** Rundschreiben an den ganzen Stand (Koenigin, Rat, Diplomaten). */
export function CircularForm({ worldId }: { worldId: number }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const send = useMutation(
    trpc.alliance.circular.mutationOptions({
      onSuccess: () => {
        setBody('');
        void qc.invalidateQueries({ queryKey: trpc.chat.pathKey() });
      },
    }),
  );
  return (
    <Card title="Rundschreiben">
      <p className="mb-2 text-xs text-stone-400">
        Erscheint markiert in #allgemein und geht als Push an alle Mitglieder.
      </p>
      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim().length >= 3) send.mutate({ worldId, body });
        }}
      >
        <textarea
          className="min-h-20 rounded-md border border-stone-700 bg-stone-900 px-2 py-1 text-sm"
          maxLength={2000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Was alle wissen müssen …"
        />
        <div className="flex items-center gap-2">
          <Button
            type="submit"
            size="sm"
            disabled={body.trim().length < 3 || send.isPending}
            loading={send.isPending}
          >
            📣 An alle senden
          </Button>
          {send.isSuccess && <span className="text-xs text-emerald-400">Gesendet.</span>}
          {send.error && <span className="text-xs text-red-400">{send.error.message}</span>}
        </div>
      </form>
    </Card>
  );
}
